<template>
  <form class="grid grid-cols-1 gap-0">
    <div id="top"></div>
    <div
      v-if="showError"
      class="bg-red-100 text-red-900 p-4 border border-red-900 my-2"
    >
      Please review the form and address any issues.
    </div>
    <h2 class="text-xl text-gray-800 font-bold pt-6">Contact Information</h2>
    <div class="text-sm italic mb-4">
      Required fields marked with <span class="text-red-800">*</span>
    </div>
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-4">
        <TextInput
          v-model="formData.first_name"
          @blur="v$.formData.first_name.$touch"
          name="first_name"
          label="First Name"
          required
          :vuelidation="v$.formData.first_name"
        ></TextInput>
        <TextInput
          v-model="formData.last_name"
          @blur="v$.formData.last_name.$touch"
          name="last_name"
          label="Last Name"
          required
          :vuelidation="v$.formData.last_name"
        ></TextInput>
        <TextInput
          v-model="formData.phone_number"
          @blur="v$.formData.phone_number.$touch"
          name="phone_number"
          label="Mobile Number"
          required
          inputmode="tel"
          placeholder="(615) 555-5555"
          description="By providing a mobile number, you give the Baking Notification Project permission to contact you using automated text messages to provide baking notifications, which may include offers and details for pickup."
          :vuelidation="v$.formData.phone_number"
        ></TextInput>
        <TextInput
          v-model="formData.phone_number_confirm"
          @blur="v$.formData.phone_number_confirm.$touch"
          name="phone_number_confirm"
          label="Confirm Mobile Number"
          required
          inputmode="tel"
          placeholder="(615) 555-5555"
          :vuelidation="v$.formData.phone_number_confirm"
        ></TextInput>
        <TextInput
          v-model="formData.email_address"
          @blur="emailBlur"
          name="email_address"
          label="Email Address"
          description="We may contact you about your subscription or send updates about the Baking Notification Project."
          required
          :vuelidation="v$.formData.email_address"
        ></TextInput>
      </div>

      <div v-if="formType === 'full'" class="flex flex-col gap-4">
        <TextInput
          v-model="formData.address.street"
          @blur="v$.formData.address.street.$touch"
          name="street_address"
          label="Street Address"
          required
          :vuelidation="v$.formData.address.street"
        ></TextInput>
        <TextInput
          v-model="formData.address.city"
          @blur="v$.formData.address.city.$touch"
          name="city"
          label="City"
          required
          :vuelidation="v$.formData.address.city"
        ></TextInput>
        <TextInput
          v-model="formData.address.state"
          @blur="v$.formData.address.state.$touch"
          name="state"
          label="State"
          description="Abbreviation (TN, NY, etc)"
          required
          :maxlength="2"
          :vuelidation="v$.formData.address.state"
        ></TextInput>
        <TextInput
          v-model="formData.address.zip"
          @blur="v$.formData.address.zip.$touch"
          name="zip_code"
          label="Zip Code"
          required
          :vuelidation="v$.formData.address.zip"
        ></TextInput>

        <SelectInput
          v-model="formData.birthmonth"
          name="birthmonth"
          label="Celebration Month"
          description="Choose your birthday month, your anniversary month, your favorite holiday month, or another time during the year when you would like to participate in an exclusive celebration bake."
          placeholder="Please select one"
          :options="[
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ]"
        ></SelectInput>

        <label class="block mb-4">
          <span class="text-gray-800 text-base font-semibold block"
            >Favorite Cake</span
          >
          <select
            v-model="formData.favorite_cake"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          >
            <option disabled value="">Please select one</option>
            <option value="Caramel">Caramel</option>
            <option value="Carrot">Carrot</option>
            <option value="Chocolate">Chocolate</option>
            <option value="Coconut">Coconut</option>
            <option value="Red Velvet">Red Velvet</option>
            <option value="Yellow with Chocolate Frosting">
              Yellow with Chocolate Frosting
            </option>
            <option value="Other">Other</option>
          </select>
        </label>

        <TextInput
          v-if="formData.favorite_cake === 'Other'"
          v-model="formData.favorite_cake_other"
          name="favorite_cake"
          placeholder="What's your favorite cake?"
        ></TextInput>

        <TextInput
          v-model="formData.favorite_baked_good"
          name="favorite_baked_good"
          label="Favorite Non-Cake Baked Good"
        ></TextInput>

        <component
          :is="PaymentProcessor"
          :baker="baker"
          :is-square-loaded="isSquareLoaded"
          :subscription-costs="subscriptionCosts"
          :creditCardError="creditCardError"
          @squareCardCreated="cardCreated($event)"
        ></component>
      </div>

      <CheckboxInput
        v-model="formData.opt_in"
        :required="true"
        name="opt_in"
        label="I agree to receive text messages to the phone number provided above."
        description="I understand that I will receive at least two messages a month, data rates may apply. Reply 'CANCEL' to end your subscription at any time."
        :vuelidation="v$.formData.opt_in"
      />

      <CheckboxInput
        v-model="formData.newsletter_signup"
        label="Sign up for the Baking Notification Project monthly newsletter"
        description="Once a month, we'll send you a newsletter to say hi, share some updates about what we've been up to with the BNP, and let you know where new bakers have joined us."
      />

      <div class="block">
        <Button
          id="subscriber-form-checkout-btn"
          :click="checkoutClick"
          button-text="Checkout"
          type="primary-large"
        />
      </div>
    </div>

    <p class="text-gray-800 my-4">
      By subscribing, you agree to the Baking Notification Project's
      <a
        href="/terms-of-service"
        target="_blank"
        class="text-blue-600 underline"
        >Terms of Service
      </a>
      .
    </p>
    <p class="italic text-sm text-gray-700">
      The Baking Notification Project is not responsible for the accuracy of the
      information and labeling of baked goods delivered to Subscribers.
      Subscribers should be advised that baked goods may be prepared in
      facilities using the same equipment that is used to prepare baked goods
      containing other allergens even if the allergen is marked as being absent
      from the food. The Baking Notification Project shall not be liable for any
      illness, health problem, or other damages that may result from any order
      or consumption of any baked good and related items obtained through the
      Service.
    </p>
  </form>
</template>
<script>
import {
  email,
  required,
  sameAs,
  maxLength,
  helpers,
} from "@vuelidate/validators";
import { merge as _merge } from "lodash";
import TextInput from "./form/TextInput.vue";
import SelectInput from "./form/SelectInput.vue";
import SquareCreditCard from "./form/SquareCreditCard.vue";
import Button from "./form/Button.vue";
import PaymentProcessorSquare from "./PaymentProcessorSquare.vue";
import PaymentProcessorNone from "./PaymentProcessorNone.vue";
import useVuelidate from "@vuelidate/core";

import axios from "../axios";
import CheckboxInput from "~/components/form/CheckboxInput.vue";

const $axios = axios();

export default {
  name: "SubscriberForm",
  components: {
    CheckboxInput,
    TextInput,
    SelectInput,
    SquareCreditCard,
    Button,
    PaymentProcessorSquare,
    PaymentProcessorNone,
  },
  props: {
    initFormData: {
      type: Object,
      default() {
        return {
          address: {},
          billing_address: {},
          billing_address_same_as_address: true,
          newsletter_signup: true,
        };
      },
    },
    formType: {
      type: String,
      default: "full",
    },
    baker: {
      type: Object,
      default() {
        return {};
      },
    },
    isSquareLoaded: {
      type: Boolean,
      default: false,
    },
    subscriptionCosts: {
      type: Object,
      default() {
        return {
          original: 10,
          currentCharge: 10,
          promoCodePrice: 0,
          specialNotice: null,
        };
      },
    },
    creditCardError: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  computed: {
    PaymentProcessor() {
      switch (this.baker.payment_processor) {
        case "square":
          return "PaymentProcessorSquare";
        case "none":
        default:
          return "PaymentProcessorNone";
      }
    },
  },
  data() {
    return {
      showError: false,
      promo_code_message: null,
      promo_code_used_code: null,
      formStatus: "ready",
      formData: {
        first_name: "",
        last_name: "",
        phone_number: "",
        email_address: "",
        address: {
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        billing_address_same_as_address: true,
        billing_address: {
          name: "",
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        birthmonth: "",
        favorite_cake: "",
        favorite_cake_other: "",
        favorite_baked_good: "",
        accepted_terms_of_service: false,
        project_id: null,
        promo_code: "",
        promo_code_uuid: null,
        newsletter_signup: true,
        opt_in: false,
      },
    };
  },
  validations() {
    const myRequired = helpers.withMessage("Required field", required);
    const waitlistForm = {
      first_name: {
        required: myRequired,
      },
      last_name: {
        required: myRequired,
      },
      phone_number: {
        required: myRequired,
      },
      phone_number_confirm: {
        required: myRequired,
        sameAsPhone: sameAs(this.formData.phone_number),
      },
      email_address: {
        required: myRequired,
        email: helpers.withMessage(
          "Please enter a valid email address.",
          email,
        ),
      },
      opt_in: {
        required: myRequired,
      },
    };

    const fullForm = _merge({}, waitlistForm, {
      address: {
        street: {
          required: myRequired,
        },
        city: {
          required: myRequired,
        },
        state: {
          required: myRequired,
          maxLength: maxLength(2),
        },
        zip: {
          required: myRequired,
          maxLength: maxLength(5),
        },
      },
    });

    return {
      formData: this.formType === "waitlist" ? waitlistForm : fullForm,
    };
  },
  mounted() {
    this.formData = this.initFormData;
    if (!this.formData.billing_address) {
      this.formData.billing_address = {};
    }

    // @TODO: Refactor

    if (!window.localStorage.getItem("bnp-session")) {
      if (self.crypto.randomUUID) {
        window.localStorage.setItem("bnp-session", self.crypto.randomUUID());
      }
    }

    if (window.localStorage.getItem("bnp-session")) {
      this.formData.bnpSession = window.localStorage.getItem("bnp-session");
    }
  },
  methods: {
    cardCreated(data) {
      this.card = data;
      this.$emit("square-card-created", data);
    },
    checkoutClick() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.showError = true;
        const position = document.getElementById("top").offsetTop;
        window.scrollTo(0, position - 50);
        return;
      }

      this.showError = false;
      this.$emit("showSubtotal", this.formData);
    },
    emailBlur() {
      this.v$.formData.email_address.$touch();

      if (this.v$.formData.email_address.$error === false) {
        $axios
          .post("/api/subscribe/record", {
            email_address: this.formData.email_address,
            session_id: window.localStorage.getItem("bnp-session"),
            project_id: this.baker.id,
            first_name: this.formData.first_name,
          })
          .then((res) => {
            console.log("response", res);
          });
      }
    },
  },
};
</script>
